import { useQuery as useApolloQuery } from '@apollo/client';
import { GetNotificationPreferencesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useErrorNotification } from '@/hooks/useErrorNotification';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Flag-gated read for the per-user notification-settings modal.
 *
 * The legacy `getNotificationPreferences` GraphQL query combined TWO Hasura
 * actions in one round trip — `notificationPreferences` (the Knock user
 * preference set) and `slackNotificationConnection` (`{ connected }`). The tRPC
 * v3 migration (RSP-6497) splits these into two cleaner procedures
 * (`notifications.userPreferences.get` + `notifications.slack.getConnection`),
 * so this hook fans out to both and reassembles the combined
 * `GetNotificationPreferencesQuery` shape the modal already consumes.
 *
 * The `createQueryHook` factory only wraps a single tRPC procedure, so this
 * two-procedure combine is hand-written but follows the same contract:
 * flag-switch between tRPC and Apollo, and map the tRPC output to the GraphQL
 * result shape.
 */
export const useGetNotificationPreferences = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();

  const {
    data: preferencesData,
    isPending: preferencesLoading,
    error: preferencesError,
    refetch: refetchPreferences,
  } = useQuery({
    ...trpc.frontend.notifications.userPreferences.get.queryOptions(),
    enabled: trpcEnabled,
  });

  const {
    data: slackData,
    isPending: slackLoading,
    error: slackError,
    refetch: refetchSlack,
  } = useQuery({
    ...trpc.frontend.notifications.slack.getConnection.queryOptions(),
    enabled: trpcEnabled,
  });

  const trpcError = preferencesError ?? slackError;
  useErrorNotification(trpcError, trpcEnabled);

  const mappedTrpcData = useMemo(() => {
    if (!preferencesData || !slackData) {
      return undefined;
    }

    return {
      __typename: 'query_root' as const,
      notificationPreferences: {
        __typename: 'NotificationPreferencesOutput' as const,
        id: preferencesData.id,
        channel_types: preferencesData.channel_types,
        categories: preferencesData.categories,
        workflows: preferencesData.workflows,
      },
      slackNotificationConnection: {
        __typename: 'SlackNotificationConnectionOutput' as const,
        connected: slackData.connected,
      },
    };
  }, [preferencesData, slackData]);

  const {
    data: graphqlData,
    loading: graphqlLoading,
    error: graphqlError,
    refetch: refetchGraphql,
  } = useApolloQuery(GetNotificationPreferencesDocument, {
    fetchPolicy: 'no-cache',
    skip: trpcEnabled,
  });

  useErrorNotification(graphqlError, !trpcEnabled);

  const refetch = useCallback(async () => {
    if (trpcEnabled) {
      return Promise.all([refetchPreferences(), refetchSlack()]);
    }

    return refetchGraphql();
  }, [trpcEnabled, refetchPreferences, refetchSlack, refetchGraphql]);

  if (trpcEnabled) {
    return {
      loading: preferencesLoading || slackLoading,
      data: mappedTrpcData,
      refetch,
      error: trpcError,
    };
  }

  return {
    loading: graphqlLoading,
    data: graphqlData,
    refetch,
    error: graphqlError,
  };
};
